import client from "./client";

export type PayrollFormat = "adp" | "gusto" | "paychex";

/**
 * Trigger a browser download of a payroll-period CSV in the chosen vendor's
 * dialect. Returns nothing — the caller hands off to the user's download
 * stream and is done.
 */
export async function downloadPayrollCsv(
  fmt: PayrollFormat,
  periodStart: string,
  periodEnd: string,
): Promise<void> {
  const res = await client.get<Blob>("/exports/payroll.csv", {
    params: { fmt, period_start: periodStart, period_end: periodEnd },
    responseType: "blob",
  });
  const blob = res.data;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `payroll-${fmt}-${periodStart}-${periodEnd}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Pull the full org export as JSON and stream it to disk. Kept for backwards
 * compatibility with automation that prefers a single JSON blob. Browser
 * callers should prefer ``downloadOrgExportZip`` which also includes the raw
 * uploaded documents.
 */
export async function downloadOrgExport(): Promise<void> {
  const res = await client.get("/exports/organization");
  const blob = new Blob([JSON.stringify(res.data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  a.download = `org-export-${ts}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Full org export as a ZIP — one JSON file per module plus a
 * ``documents/`` folder with the raw uploaded files. This is what the
 * Settings → Organization "Export all data" button calls. The server
 * writes an audit-log entry for each successful download.
 */
export async function downloadOrgExportZip(): Promise<void> {
  const res = await client.get<Blob>("/exports/organization.zip", {
    responseType: "blob",
  });
  const blob = res.data;
  // Prefer the filename the server picked — it already embeds org slug +
  // UTC timestamp. Fall back to a generic name if the header is missing
  // (axios strips unknown headers on some CORS configs).
  const disposition = (res.headers as Record<string, string>)[
    "content-disposition"
  ];
  const match = disposition?.match(/filename="?([^";]+)"?/i);
  const filename =
    match?.[1] ??
    `lenavio-export-${new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, 19)}.zip`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
